import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FileText, GraduationCap, Heart, Droplets, FolderKanban, Calendar, Image, ArrowRight } from 'lucide-react';
import PageSEO from '../components/PageSEO';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const MAYOR_PHOTO = "https://customer-assets.emergentagent.com/job_e685b0f8-6e4a-4c34-8412-b6505cc3b7ac/artifacts/i579cojd_mr%20le%20maire%201.jpg";

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/uploads/') || url.startsWith('/api/files/')) return `${BASE_URL}${url}`;
  return url;
};

const quickServices = [
  { icon: FileText, label: 'État civil', path: '/mairie/demarches', desc: "Accédez rapidement aux informations et démarches liées à l'état civil." },
  { icon: FileText, label: 'Urbanisme', path: '/mairie/services', desc: "Accédez rapidement aux informations et démarches liées à l'urbanisme." },
  { icon: Heart, label: 'Santé', path: '/vie-quotidienne', desc: 'Accédez rapidement aux informations et démarches liées à la santé.' },
  { icon: GraduationCap, label: 'Éducation', path: '/vie-quotidienne', desc: "Accédez rapidement aux informations et démarches liées à l'éducation." },
  { icon: Droplets, label: 'Eau & assainissement', path: '/vie-quotidienne', desc: "Accédez rapidement aux informations et démarches liées à l'eau." },
  { icon: FolderKanban, label: 'Projets communaux', path: '/projets', desc: 'Accédez rapidement aux informations et démarches liées aux projets.' },
  { icon: Calendar, label: 'Agenda', path: '/medias?tab=agenda', desc: "Accédez rapidement aux informations et démarches liées à l'agenda." },
  { icon: Image, label: 'Médiathèque', path: '/medias?tab=galerie', desc: 'Accédez rapidement aux informations et démarches liées à la médiathèque.' },
];

export const HomePage = () => {
  const [news, setNews] = useState([]);
  const [projects, setProjects] = useState([]);
  const [page, setPage] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [newsRes, projectsRes, pageRes] = await Promise.all([
        axios.get(`${API}/news?limit=3`),
        axios.get(`${API}/projects?limit=2`),
        axios.get(`${API}/pages/accueil`),
      ]);
      setNews(newsRes.data);
      setProjects(projectsRes.data);
      setPage(pageRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getSection = (key) => page?.sections?.find(s => s.key === key);
  const heroTitle = getSection('hero_title')?.content || 'Commune de Gandiaye';
  const heroSubtitle = getSection('hero_subtitle')?.content || 'Bienvenue sur le portail officiel de la municipalité. Découvrez les services, les actualités et les projets de la commune.';
  const motDuMaire = getSection('mot_du_maire')?.content || '';
  const diasporaCta = getSection('diaspora_cta')?.content || '';

  return (
    <main className="min-h-screen bg-slate-50 text-slate-800">
      <PageSEO
        title="Accueil"
        description="Portail officiel de la Commune de Gandiaye, Sénégal. Actualités, démarches administratives, projets de développement et services municipaux."
        path="/"
      />

      {/* HERO */}
      <section className="relative isolate overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="/gandiaye-hero.jpg"
            alt="Commune de Gandiaye"
            className="h-full w-full object-cover object-center"
          />
          {/* Overlay uniforme léger */}
          <div className="absolute inset-0 bg-black/40" />
          {/* Dégradé bas pour mobile */}
          <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black/55 via-black/20 to-transparent lg:hidden" />
        </div>

        <div className="relative mx-auto flex min-h-[72vh] max-w-7xl items-end px-4 py-10 sm:py-14 lg:min-h-[78vh] lg:items-center lg:justify-center lg:px-8">
          <div className="max-w-xl text-center text-white">
            <h2 className="hero-title text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl" style={{ fontFamily: 'var(--font-heading)' }}>
              {heroTitle}
            </h2>
            <p className="mt-4 text-lg md:text-xl text-white/[0.92]">
              {heroSubtitle}
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:gap-4 justify-center">
              <Link
                to="/decouvrir"
                className="inline-flex items-center justify-center rounded-lg bg-green-600 px-6 py-3 text-base font-semibold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-green-700 hover:shadow-xl"
              >
                Découvrir Gandiaye
              </Link>
              <Link
                to="/medias"
                className="inline-flex items-center justify-center rounded-lg border border-white px-6 py-3 text-base font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/10"
              >
                Voir les actualités
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK SERVICES */}
      <section className="mx-auto max-w-7xl px-4 py-16 lg:px-8">
        <div className="mb-8">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#0F6B3E]">
            Services essentiels
          </p>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
            Accès rapide aux services municipaux
          </h2>
        </div>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {quickServices.map((service) => (
            <Link
              key={service.label}
              to={service.path}
              className="group rounded-3xl border border-slate-200/80 bg-white p-6 shadow-[0_8px_30px_rgba(15,107,62,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(15,107,62,0.14)]"
            >
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0F6B3E]/10">
                <service.icon className="text-[#0F6B3E]" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900">{service.label}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">{service.desc}</p>
              <span className="mt-5 inline-block font-semibold text-[#0F6B3E] transition group-hover:text-[#0A4F2D]">
                En savoir plus <ArrowRight className="inline ml-1" size={16} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* MOT DU MAIRE */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="rounded-3xl border border-slate-200 bg-[#fef9e7] p-8 md:p-12 shadow-sm">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <img
                src={MAYOR_PHOTO}
                alt="Pape Songde Diop, Maire de Gandiaye"
                loading="lazy"
                className="w-40 h-48 object-cover rounded-2xl shadow-lg flex-shrink-0"
              />
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#0F6B3E] mb-2">Mot du Maire</p>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Pape Songde Diop</h2>
                <p className="text-slate-600 leading-7">
                  {motDuMaire || 'Bienvenue sur le portail officiel de la commune de Gandiaye. Ensemble, développons notre commune pour un avenir meilleur.'}
                </p>
                <Link to="/mairie" className="mt-5 inline-block font-semibold text-[#0F6B3E] hover:text-[#0A4F2D] transition">
                  Lire le message complet <ArrowRight className="inline ml-1" size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEWS */}
      <section id="news" className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="mb-8 flex items-end justify-between gap-4">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#CE1126]">Actualités</p>
              <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">Dernières informations de la commune</h2>
            </div>
            <Link to="/medias" className="hidden rounded-xl border border-slate-300 px-4 py-2 font-medium text-slate-700 transition hover:bg-slate-50 md:inline-flex">
              Toutes les actualités
            </Link>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {news.length > 0 ? news.map((item) => (
              <article key={item.id} className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_8px_30px_rgba(15,107,62,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(15,107,62,0.14)]">
                <img
                  src={getImageUrl(item.image_url) || 'https://images.unsplash.com/photo-1504711434969-e33886168d5c?w=400'}
                  alt={item.title}
                  className="h-56 w-full object-cover"
                  loading="lazy"
                  onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168d5c?w=400'}
                />
                <div className="p-6">
                  <p className="text-sm font-medium text-[#0F6B3E]">
                    {new Date(item.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                  <h3 className="mt-2 text-xl font-bold text-slate-900 line-clamp-2">{item.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600 line-clamp-3">{item.summary || item.content}</p>
                  <Link to={`/actualites/${item.id}`} className="mt-5 inline-block font-semibold text-[#0F6B3E]">
                    Lire l'article <ArrowRight className="inline ml-1" size={16} />
                  </Link>
                </div>
              </article>
            )) : (
              [1, 2, 3].map((i) => (
                <article key={i} className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                  <div className="h-56 w-full bg-slate-200 animate-pulse" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 w-24 bg-slate-200 rounded animate-pulse" />
                    <div className="h-6 w-3/4 bg-slate-200 rounded animate-pulse" />
                    <div className="h-4 w-full bg-slate-200 rounded animate-pulse" />
                  </div>
                </article>
              ))
            )}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="mx-auto max-w-7xl px-4 py-16 lg:px-8">
        <div className="mb-8 flex items-end justify-between gap-4">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#F59E0B]">Projets</p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">Projets structurants de la commune</h2>
          </div>
          <Link to="/projets" className="hidden rounded-xl border border-slate-300 px-4 py-2 font-medium text-slate-700 transition hover:bg-slate-50 md:inline-flex">
            Tous les projets
          </Link>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {projects.length > 0 ? projects.map((project) => (
            <div key={project.id} className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-[0_8px_30px_rgba(15,107,62,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(15,107,62,0.14)]">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-900">{project.title}</h3>
                <span className="rounded-full bg-[#0F6B3E]/10 px-3 py-1 text-sm font-semibold text-[#0F6B3E]">
                  {project.status === 'termine' ? 'Terminé' : project.status === 'planifie' ? 'Planifié' : 'En cours'}
                </span>
              </div>
              <p className="text-sm leading-6 text-slate-600 line-clamp-2">{project.description}</p>
              <div className="mt-5">
                <div className="mb-2 flex items-center justify-between text-sm font-medium text-slate-600">
                  <span>Avancement</span>
                  <span>{project.progress || 0}%</span>
                </div>
                <div className="h-3 rounded-full bg-slate-200">
                  <div className="h-3 rounded-full bg-[#0F6B3E] transition-all duration-500" style={{ width: `${project.progress || 0}%` }} />
                </div>
              </div>
            </div>
          )) : (
            [1, 2].map((i) => (
              <div key={i} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="h-6 w-2/3 bg-slate-200 rounded animate-pulse mb-4" />
                <div className="h-4 w-full bg-slate-200 rounded animate-pulse mb-2" />
                <div className="mt-5 h-3 rounded-full bg-slate-200" />
              </div>
            ))
          )}
        </div>
      </section>

      {/* CTA DIASPORA */}
      <section className="bg-[#0F6B3E] py-16">
        <div className="mx-auto max-w-7xl px-4 text-center lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-4">Espace Diaspora</h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto text-lg">
            {diasporaCta || 'Fils et filles de Gandiaye établis dans le monde, contribuez au développement de notre commune.'}
          </p>
          <Link to="/diaspora" className="inline-flex items-center justify-center rounded-2xl bg-[#FCD116] px-8 py-4 text-lg font-semibold text-slate-900 shadow-lg transition duration-300 hover:-translate-y-0.5 hover:shadow-xl">
            Rejoindre l'Espace Diaspora
          </Link>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
